import { X } from 'lucide-react';
import { useEffect, useEffectEvent } from 'react';

const HANDOFF_EXPIRY_MS = 5 * 60 * 1000;

interface HandoffData {
  access_token: string;
  expires_in: number;
  timestamp?: number;
  session_id?: string;
  redirect_path?: string;
}

/** Validate handoff data from localStorage (pure read). */
const validateHandoff = ():
  | { success: true; data: HandoffData; redirectPath: string }
  | { success: false; error: string } => {
  const handoffData = localStorage.getItem('impersonation_handoff');

  if (!handoffData) {
    return {
      success: false,
      error:
        'No impersonation data found. Please try again from the admin panel.',
    };
  }

  try {
    const data: HandoffData = JSON.parse(handoffData);

    if (!data.access_token) {
      return { success: false, error: 'Invalid handoff data: missing token' };
    }

    const handoffAge = Date.now() - (data.timestamp || 0);
    if (handoffAge > HANDOFF_EXPIRY_MS) {
      return {
        success: false,
        error: 'Impersonation link has expired. Please try again.',
      };
    }

    return {
      success: true,
      data,
      redirectPath: data.redirect_path || '/portal',
    };
  } catch {
    return {
      success: false,
      error: 'Failed to process impersonation',
    };
  }
};

/** Store tokens and clear handoff data. */
const applyHandoff = (data: HandoffData) => {
  localStorage.removeItem('investor_portal_token');
  localStorage.removeItem('investor_portal_refresh_token');
  localStorage.removeItem('investor_portal_token_expires_at');
  localStorage.removeItem('investor_portal_contact');

  localStorage.setItem('investor_portal_token', data.access_token);

  const expiresAt = Date.now() + data.expires_in * 1000;
  localStorage.setItem(
    'investor_portal_token_expires_at',
    expiresAt.toString()
  );

  if (data.session_id) {
    localStorage.setItem('investor_portal_session_id', data.session_id);
  }

  localStorage.removeItem('impersonation_handoff');
};

/**
 * Impersonation Handoff Page
 *
 * Receives impersonation tokens from the admin app via localStorage,
 * sets up the portal session, and redirects. On failure, shows an error.
 */
export function ImpersonationHandoffPage() {
  const validation = validateHandoff();

  const onHandoff = useEffectEvent(() => {
    if (validation.success) {
      applyHandoff(validation.data);
      window.location.replace(validation.redirectPath);
    }
  });

  useEffect(() => {
    onHandoff();
  }, []);

  if (validation.success) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-8">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Impersonation Failed
          </h2>
          <p className="text-gray-600 mb-4">{validation.error}</p>
          <button
            onClick={() => window.close()}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Close Window
          </button>
        </div>
      </div>
    </div>
  );
}
